import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';

import APP_ROUTES from '@src/app_routes';
import { FEATURES } from '@src/plans-permissions';
import { InsightsFeatureRenderer } from '@src/hoc/InsightsFeatureRenderer';
import {
  QUALITATIVE_TEMPLATE_KEYS,
  STRONG_SCORE,
  TEMPLATE_DESCRIPTIONS,
  averageScore,
  formatClipTime,
  insightsReportPath,
  normalizeLabel,
  overallLabel,
  overallOfTemplates,
  overallScale,
  scalesByTemplate,
  scoreOfTemplate,
  toDisplayScore
} from '@src/constants/insights';
import { useMediaTableContext } from '@src/hooks/contexts/useMediaTableContext';
import {
  useGenerateResponseSummary,
  useInsightsTemplateCatalog,
  useResponseInsightsScores,
  useResponseSummary
} from '@src/hooks/react-query/useInsightsEvaluationData';
import { InsightsUpgradePrompt } from '@vp-ui/components/InsightsUpgradePrompt';
import { VpButton } from '@vp-ui/components/VpButton';
import { VpClipChip } from '@vp-ui/components/VpClipChip';
import { VpScoreBadge, VpScoreCell, VpScoreLegend, VpScoreRing } from '@vp-ui/components/VpScoreRing';
import { VpVideoPlayer } from '@vp-ui/components/VpVideoPlayer/VpVideoPlayer';

const cardSx = { borderRadius: '5px', borderColor: 'vpColors.greyLight' };

// A clip is a stretch of the original video: the browser plays from start and stops at end (media
// fragment), so no clip file is ever made.
const clipUrl = (mediaUrl, start, end) => `${mediaUrl}#t=${start}${end !== null && end !== undefined ? `,${end}` : ''}`;

const momentStart = (momentUrl) => {
  const start = Number((momentUrl || '').split('#t=')[1]);
  return Number.isFinite(start) ? start : null;
};

const isStrong = (score) => score !== null && score !== undefined && toDisplayScore(score) >= STRONG_SCORE;

// A template's best moment: its highest-scoring dimension with a clip, and only when that is a strong result —
// the moment should show the answer at its best. Custom has no dimensions, so its own evidence stands in
// when its score is strong. A signal score is not a good result, so signal templates have no best moment.
const bestMoment = (templateKey, templateName, result, scale) => {
  if (scale === 'signal') return null;
  const best = (result.dimensions || [])
    .filter((dimension) => !dimension.informational && dimension.clipStart !== null && isStrong(dimension.score))
    .sort((a, b) => b.score - a.score)[0];
  if (best) return { label: `${templateName} · ${best.name}`, start: best.clipStart, end: best.clipEnd, score: best.score };

  const start = momentStart(result.templateScoreVideoUrl);
  if (templateKey === 'custom' && start !== null && isStrong(scoreOfTemplate(result))) {
    return { label: `${templateName} · best moment`, start, end: null, score: scoreOfTemplate(result) };
  }
  return null;
};

// One player for the whole tab, made on the first clip played; each play gets a fresh one (its key),
// since the player does not reload when only its source changes, and a clip played again must restart.
const ClipPlayer = ({ mediaUrl, clip, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [clip]);

  const url = clipUrl(mediaUrl, clip.start, clip.end);
  return (
    <Paper ref={ref} variant="outlined" sx={{ p: 2.4, display: 'flex', flexDirection: 'column', gap: 1.6, ...cardSx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.6}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
          {clip.label} · {formatClipTime(clip.start)}{clip.end !== null && clip.end !== undefined ? `–${formatClipTime(clip.end)}` : ''}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close clip"><CloseIcon /></IconButton>
      </Stack>
      <VpVideoPlayer key={`${url}-${clip.play}`} mediaUrl={url} autoPlay noDownload />
    </Paper>
  );
};

ClipPlayer.propTypes = {
  mediaUrl: PropTypes.string.isRequired,
  clip: PropTypes.shape({ label: PropTypes.string, start: PropTypes.number, end: PropTypes.number, play: PropTypes.number }).isRequired,
  onClose: PropTypes.func.isRequired
};

const ClipButton = ({ label, start, onPlay }) => (
  <Stack direction="row">
    <VpClipChip label={formatClipTime(start)} ariaLabel={`Play ${label} clip at ${formatClipTime(start)}`} onClick={onPlay} />
  </Stack>
);

ClipButton.propTypes = { label: PropTypes.string.isRequired, start: PropTypes.number.isRequired, onPlay: PropTypes.func.isRequired };

// Informational dimensions use their own 1-10 scale and stay out of the score, so no score band.
const DimensionScore = ({ dimension, scale }) => {
  if (dimension.informational && dimension.score !== null) {
    return (
      <Tooltip describeChild title="Own scale, not part of the template score">
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{Math.round(dimension.score * 10) / 10}/10</Typography>
      </Tooltip>
    );
  }
  return <VpScoreCell score={toDisplayScore(dimension.score)} notApplicable={dimension.score === null} label={dimension.name} scale={scale} />;
};

DimensionScore.propTypes = {
  dimension: PropTypes.shape({ name: PropTypes.string, score: PropTypes.number, informational: PropTypes.bool }).isRequired,
  scale: PropTypes.string
};

// A qualitative dimension is not scored: it names what the answer expressed, in up to three short labels.
// Filled chips, unlike the outlined clip chips, since they play nothing. The theme gives filled chips a
// vertical margin; the stack's gap spaces them instead.
const DimensionLabels = ({ labels }) => (labels.length ? (
  <Stack direction="row" flexWrap="wrap" gap={0.8}>
    {labels.map((label) => (
      <Chip
        key={label}
        size="small"
        label={label}
        sx={{ m: 0, height: 'auto', borderRadius: '5px', bgcolor: 'vpColors.greyLighter', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.4 } }}
      />
    ))}
  </Stack>
) : (
  <Typography variant="caption" color="text.secondary">Not mentioned in this answer</Typography>
));

DimensionLabels.propTypes = { labels: PropTypes.arrayOf(PropTypes.string).isRequired };

const DimensionTile = ({ dimension, scale, onPlay }) => (
  <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.2, minWidth: 0, ...cardSx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.2}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>{dimension.name}</Typography>
      {!dimension.qualitative && <DimensionScore dimension={dimension} scale={scale} />}
    </Stack>
    {dimension.qualitative && <DimensionLabels labels={dimension.labels || []} />}
    {dimension.clipStart !== null && onPlay && (
      <ClipButton label={dimension.name} start={dimension.clipStart} onPlay={() => onPlay({ label: dimension.name, start: dimension.clipStart, end: dimension.clipEnd })} />
    )}
  </Paper>
);

DimensionTile.propTypes = {
  dimension: PropTypes.shape({
    name: PropTypes.string,
    qualitative: PropTypes.bool,
    labels: PropTypes.arrayOf(PropTypes.string),
    clipStart: PropTypes.number,
    clipEnd: PropTypes.number
  }).isRequired,
  scale: PropTypes.string,
  onPlay: PropTypes.func
};

// A qualitative template is never scored: its findings are the labels under Key characteristics.
const TemplateCard = ({ name, description, score, scale, qualitative, moment, onPlay }) => (
  <Paper variant="outlined" sx={{ p: 3.2, display: 'flex', flexDirection: 'column', gap: 2.4, minWidth: 0, ...cardSx }}>
    <Stack direction="row" alignItems="center" spacing={2.4}>
      <VpScoreRing score={score} size={72} scale={scale} />
      <Stack spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
        {score === null ? (
          <Typography variant="caption" color="text.secondary">{qualitative ? 'Qualitative insights' : 'Not applicable to this answer'}</Typography>
        ) : <VpScoreBadge score={score} scale={scale} />}
      </Stack>
    </Stack>
    {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
    {moment && onPlay && (
      <Stack direction="row" sx={{ mt: 'auto' }}>
        <VpClipChip label="Best moment" ariaLabel={`Play the best moment of ${name}`} onClick={() => onPlay(moment)} />
      </Stack>
    )}
  </Paper>
);

TemplateCard.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  score: PropTypes.number,
  scale: PropTypes.string,
  qualitative: PropTypes.bool,
  moment: PropTypes.shape({ label: PropTypes.string, start: PropTypes.number, end: PropTypes.number }),
  onPlay: PropTypes.func
};

// A 409 says what the response is still waiting for (a transcript or an evaluation), in the API's words.
const summaryError = (error) => {
  const status = error?.response?.status;
  if (status === 400) return 'This response has no transcribed video to summarize.';
  if (status === 403) return 'AI Insights is not available on this plan.';
  if (status === 409) return error.response.data?.message || 'This response is not ready to be summarized yet. Try again later.';
  return 'Unable to generate the summary.';
};

// A short AI text about the person and the response, from all of its videos, made once and kept. It is made on
// its own the first time the tab opens with every video evaluated, so it can speak to the evaluated
// characteristics; before that, someone can still ask for it from the transcripts.
const ResponseSummary = ({ submissionId, autoGenerate }) => {
  const summaryQuery = useResponseSummary(submissionId);
  const generate = useGenerateResponseSummary(submissionId);
  const missing = summaryQuery.isError && summaryQuery.error?.response?.status === 404;
  // One automatic try per opening: a failure waits for the person instead of looping.
  const autoTried = useRef(false);

  useEffect(() => {
    if (!missing || !autoGenerate || autoTried.current) return;
    autoTried.current = true;
    generate.mutate();
  }, [missing, autoGenerate]);

  let body;
  if (summaryQuery.isLoading) {
    body = <Typography variant="body2" color="text.secondary">Loading summary...</Typography>;
  // A 404 after a summary was shown means it no longer holds (the rubrics changed): the query still has the old
  // text, which must not stand in for the new one or hide a failed regeneration.
  } else if (!summaryQuery.isError && summaryQuery.data?.summary) {
    body = (
      <>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{summaryQuery.data.summary}</Typography>
        <Typography variant="caption" color="text.secondary">AI-generated from the videos of this response.</Typography>
      </>
    );
  // After a failed refetch the query still holds its last data: the error, not an old "generating", decides.
  } else if ((!summaryQuery.isError && summaryQuery.data?.status === 'generating') || (missing && generate.isLoading)) {
    body = <Typography variant="body2" color="text.secondary">Generating summary...</Typography>;
  } else if (missing) {
    body = (
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.6}>
        <Typography variant="body2" color={generate.isError ? 'text.primary' : 'text.secondary'}>
          {generate.isError
            ? summaryError(generate.error)
            : 'A short AI summary of this person and their answers, from all the videos of this response.'}
        </Typography>
        <VpButton variant="outlined" size="small" onClick={() => generate.mutate()}>
          {generate.isError ? 'Try again' : 'Generate summary'}
        </VpButton>
      </Stack>
    );
  } else {
    body = (
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.6}>
        <Typography variant="body2">Unable to load the summary.</Typography>
        <VpButton variant="outlined" size="small" onClick={() => summaryQuery.refetch()}>Retry</VpButton>
      </Stack>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3.6, display: 'flex', flexDirection: 'column', gap: 1.6, ...cardSx }}>
      <Stack direction="row" alignItems="center" spacing={1.6}>
        <NotesOutlinedIcon color="primary" />
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>Response summary</Typography>
      </Stack>
      {body}
    </Paper>
  );
};

ResponseSummary.propTypes = { submissionId: PropTypes.string.isRequired, autoGenerate: PropTypes.bool };

const overallCaption = (scoredCount, scale, qualitativeOnly, videoCount) => {
  if (qualitativeOnly) return 'Qualitative insights only: what the answer expressed is under Key characteristics.';
  const label = overallLabel(scale);
  if (scoredCount === 0) return `No template applied to this answer, so there is no ${label.toLowerCase()}.`;
  const templates = scoredCount === 1 ? 'template' : `${scoredCount} templates`;
  // A response with several videos is scored as a whole: each template's score is the mean of its videos.
  const across = videoCount > 1 ? `, across the ${videoCount} videos of this response` : '';
  return `${label}: average of the ${templates} that could be scored${across}.`;
};

const isQualitativeTemplate = (key, results) =>
  QUALITATIVE_TEMPLATE_KEYS.includes(key)
  || (results.some((result) => result.dimensions?.length) && results.every((result) => (result.dimensions || []).every((dimension) => dimension.qualitative)));

// One characteristic for the whole response, from every video that has it: the score is the mean of the
// videos (the rule the template scores follow), the labels are all of theirs, and the clip comes from the video
// where it scored best, to be played in that video. With one video it is that video's dimension.
const mergeDimensions = (found) => {
  const byKey = new Map();
  found.forEach((each) => byKey.set(each.dimension.key, [...(byKey.get(each.dimension.key) || []), each]));
  return [...byKey.values()].map((entries) => {
    const clipped = entries
      .filter(({ dimension }) => dimension.clipStart !== null && dimension.clipStart !== undefined)
      .sort((a, b) => (b.dimension.score ?? -1) - (a.dimension.score ?? -1))[0];
    const labels = new Map();
    entries.forEach(({ dimension }) => (dimension.labels || []).forEach((label) => {
      if (!labels.has(normalizeLabel(label))) labels.set(normalizeLabel(label), label);
    }));
    return {
      ...entries[0].dimension,
      score: averageScore(entries.map(({ dimension }) => dimension.score)) ?? null,
      labels: [...labels.values()],
      clipStart: clipped ? clipped.dimension.clipStart : null,
      clipEnd: clipped ? clipped.dimension.clipEnd : null,
      clipVideo: clipped?.video
    };
  });
};

// One Insights tab for the whole response: videos is every video the respondent sent, in the response's
// order. With one evaluated video the tab is that video's evaluation; with several, each template's score and
// each characteristic's is the mean of the videos (the rule the ranking uses), shown once for the response.
export const InsightsContent = ({ mediaId, videos = null, initialClip = null, submissionId = null, campaignLink = null }) => {
  const responseVideos = videos?.length ? videos : [{ mediaId }];
  const scoreQueries = useResponseInsightsScores(responseVideos.map((video) => video.mediaId));
  const catalogQuery = useInsightsTemplateCatalog();
  const [clip, setClip] = useState(initialClip && { ...initialClip, play: 0 });
  const plays = useRef(0);

  if (scoreQueries.some((query) => query.isLoading)) return <Typography>Loading Insights...</Typography>;

  // A video whose scores failed to load would silently drop out of the means: the response's scores are
  // shown whole or not at all. Only a video without an evaluation (404) is legitimately left out.
  const failed = scoreQueries.filter((query) => query.isError && query.error?.response?.status !== 404);
  if (failed.length) {
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.6}>
        <Typography>Unable to load Insights scores.</Typography>
        <VpButton variant="outlined" size="small" onClick={() => failed.forEach((query) => query.refetch())}>Retry</VpButton>
      </Stack>
    );
  }

  const evaluated = responseVideos
    .map((video, index) => ({ ...video, scores: scoreQueries[index]?.data }))
    .filter((video) => video.scores);
  // The summary reads the transcripts, not the evaluations: it is offered before any video is evaluated too.
  if (!evaluated.length) {
    return (
      <Stack spacing={3.6}>
        <Typography>Insights are not available for this video yet.</Typography>
        {submissionId && <ResponseSummary submissionId={submissionId} />}
      </Stack>
    );
  }

  const single = evaluated.length === 1;
  const names = Object.fromEntries((catalogQuery.data || []).map((template) => [template.key, template.name]));
  const scales = scalesByTemplate(catalogQuery.data);
  const scaleOf = (key) => scales[key] || 'quality';
  const templatesOf = (video) => Object.entries(video.scores.templates || {});
  // In the catalog's order, as the ranking and the editor list them: the evaluation stores templates in no set order.
  const catalogOrder = (catalogQuery.data || []).map((template) => template.key);
  const templateKeys = [...new Set(evaluated.flatMap((video) => templatesOf(video).map(([key]) => key)))]
    .sort((a, b) => catalogOrder.indexOf(a) - catalogOrder.indexOf(b));
  // undefined: that video was not evaluated with the template, so it does not count for its mean.
  const scoreIn = (video, key) => (video.scores.templates?.[key] ? scoreOfTemplate(video.scores.templates[key]) : undefined);
  const templateScores = Object.fromEntries(templateKeys.map((key) => [key, averageScore(evaluated.map((video) => scoreIn(video, key))) ?? null]));
  // One video keeps the overall the lambda stored for it.
  const overall = toDisplayScore(single ? evaluated[0].scores.overallScore : overallOfTemplates(templateScores, scales));
  const scale = overallScale(templateKeys, scales);
  // Only templates with a score enter the average, N/A ones do not; with quality templates present the
  // signal ones stay out of it too.
  const scoredCount = templateKeys.filter((key) => scaleOf(key) === scale && templateScores[key] !== null).length;
  const resultsOf = (key) => evaluated.map((video) => video.scores.templates?.[key]).filter(Boolean);
  const qualitativeOnly = templateKeys.length > 0 && templateKeys.every((key) => isQualitativeTemplate(key, resultsOf(key)));
  const scalesOnScreen = ['quality', 'signal'].filter((each) => templateKeys.some((key) => scaleOf(key) === each));
  // A clip belongs to one video of the response and plays only there: its times mean nothing in another
  // video. A clip that names no video can only be the single evaluated one's.
  const videoOfClip = (clipMediaId) => (clipMediaId
    ? evaluated.find((video) => video.mediaId === clipMediaId)
    : single ? evaluated[0] : undefined);
  const mediaUrlOf = (clipMediaId) => videoOfClip(clipMediaId)?.scores.mediaUrl || null;
  const playIn = (video) => (video.scores.mediaUrl
    ? (next) => setClip({ ...next, mediaId: video.mediaId, play: (plays.current += 1) })
    : null);
  // A template's best moment is its best one in any video.
  const bestMomentOf = (key) => evaluated
    .map((video) => ({ video, moment: video.scores.templates?.[key] && playIn(video) && bestMoment(key, names[key] || key, video.scores.templates[key], scaleOf(key)) }))
    .filter(({ moment }) => moment)
    .sort((a, b) => b.moment.score - a.moment.score)[0];
  const characteristics = templateKeys
    .map((key) => ({
      key,
      dimensions: mergeDimensions(evaluated.flatMap((video) => (video.scores.templates?.[key]?.dimensions || []).map((dimension) => ({ video, dimension }))))
    }))
    .filter(({ dimensions }) => dimensions.length);
  const playerUrl = clip && mediaUrlOf(clip.mediaId);

  return (
    <Stack spacing={3.6}>
      {clip && playerUrl && <ClipPlayer mediaUrl={playerUrl} clip={clip} onClose={() => setClip(null)} />}
      {/* A grid rather than a wrapping row: the score stays on the right and the caption wraps instead, so
          the header keeps one shape whatever the caption's length. */}
      <Paper variant="outlined" sx={{ p: 3.6, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', alignItems: 'center', columnGap: 3.2, ...cardSx }}>
        <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '5px', bgcolor: 'vpColors.blueLighter', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <InsightsOutlinedIcon />
        </Box>
        <Stack spacing={0.4}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>AI Insights</Typography>
          <Typography variant="body2" color="text.secondary">
            {overallCaption(scoredCount, scale, qualitativeOnly, evaluated.length)}
          </Typography>
          {/* Same action as the editor's Insights tab. Opened from the ranking itself, it would go nowhere. */}
          {campaignLink && (
            <Route path={APP_ROUTES.insightsReport}>
              {({ match }) => !match && (
                <Box sx={{ pt: 0.8 }}>
                  <VpButton variant="outlined" size="small" to={insightsReportPath(campaignLink)} sx={{ '&:hover': { color: 'primary.main' } }}>
                    View ranking
                  </VpButton>
                </Box>
              )}
            </Route>
          )}
        </Stack>
        {!qualitativeOnly && <Stack alignItems="flex-end" spacing={0.8}>
          <Stack direction="row" alignItems="baseline" spacing={0.8}>
            {/* An evaluated video with no overall score had nothing applicable in any template. */}
            <Typography component="span" sx={{ fontSize: overall === null ? 24 : 32, fontWeight: 700, lineHeight: 1, color: overall === null ? 'text.secondary' : 'text.primary' }}>{overall ?? 'N/A'}</Typography>
            {overall !== null && <Typography component="span" variant="body2" color="text.secondary">/100</Typography>}
          </Stack>
          <VpScoreBadge score={overall} scale={scale} />
        </Stack>}
      </Paper>
      {submissionId && <ResponseSummary submissionId={submissionId} autoGenerate={evaluated.length === responseVideos.length} />}
      {/* Columns follow the panel's width, not the window's: the dialog panel can be narrow on a wide screen. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 3.2 }}>
        {templateKeys.map((key) => {
          const best = bestMomentOf(key);
          return (
            <TemplateCard
              key={key}
              name={names[key] || key}
              description={TEMPLATE_DESCRIPTIONS[key]}
              score={toDisplayScore(templateScores[key])}
              scale={scaleOf(key)}
              qualitative={isQualitativeTemplate(key, resultsOf(key))}
              moment={best?.moment}
              onPlay={best && playIn(best.video)}
            />
          );
        })}
      </Box>
      {characteristics.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3.6, display: 'flex', flexDirection: 'column', gap: 2.4, ...cardSx }}>
          <Stack direction="row" alignItems="center" spacing={1.6}>
            <MovieOutlinedIcon color="primary" />
            <Stack spacing={0.4}>
              <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>Key characteristics & clips</Typography>
              <Typography variant="body2" color="text.secondary">
                {single
                  ? 'Each characteristic with the moment of the answer behind it.'
                  : `Each characteristic's average across the ${evaluated.length} videos, with the moment from the video where it scored best.`}
              </Typography>
            </Stack>
          </Stack>
          {characteristics.map(({ key, dimensions }) => (
            <Stack key={key} spacing={1.2}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{names[key] || key}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.6 }}>
                {dimensions.map((dimension) => (
                  <DimensionTile
                    key={dimension.key}
                    dimension={dimension}
                    scale={scaleOf(key)}
                    onPlay={dimension.clipVideo && playIn(dimension.clipVideo)}
                  />
                ))}
              </Box>
            </Stack>
          ))}
        </Paper>
      )}
      <VpScoreLegend scales={scalesOnScreen.length ? scalesOnScreen : undefined} />
    </Stack>
  );
};

const videoShape = PropTypes.shape({ mediaId: PropTypes.string, question: PropTypes.string });

InsightsContent.propTypes = {
  mediaId: PropTypes.string,
  videos: PropTypes.arrayOf(videoShape),
  initialClip: PropTypes.shape({ mediaId: PropTypes.string, label: PropTypes.string, start: PropTypes.number, end: PropTypes.number }),
  submissionId: PropTypes.string,
  campaignLink: PropTypes.string
};

// A clip picked in the ranking plays as soon as its response's tab opens, once.
const VpMediaInfoInsights = ({ mediaId, videos, campaignLink = null }) => {
  const { pendingClip, setPendingClip, selectedSubmissionId } = useMediaTableContext();
  const mediaIds = videos?.length ? videos.map((video) => video.mediaId) : [mediaId];
  const [initialClip] = useState(() => (pendingClip && mediaIds.includes(pendingClip.mediaId) ? pendingClip : null));
  useEffect(() => {
    if (initialClip) setPendingClip(null);
  }, []);

  return (
    <InsightsFeatureRenderer feature={FEATURES.insights_evaluation_view} showUpgradePopup={false} fallback={<InsightsUpgradePrompt />}>
      <InsightsContent mediaId={mediaId} videos={videos} initialClip={initialClip} submissionId={selectedSubmissionId} campaignLink={campaignLink} />
    </InsightsFeatureRenderer>
  );
};

VpMediaInfoInsights.propTypes = { mediaId: PropTypes.string, videos: PropTypes.arrayOf(videoShape), campaignLink: PropTypes.string };

export default VpMediaInfoInsights;



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoInsights.jsx